import { Card } from '@cortex/web-design-system/components/ui/Card';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BugDetailsCard } from '../../components/features/bug-evaluation/BugDetailsCard';
import { CauseProjectCard } from '../../components/features/bug-evaluation/CauseProjectCard';
import { EvaluationResultsCard } from '../../components/features/bug-evaluation/EvaluationResultsCard';
import { LoadingSkeleton } from '../../components/features/bug-evaluation/LoadingSkeleton';
import { REVIEW_ROLES } from '../../components/features/bug-evaluation/constants';
import { ReviewForm } from '../../components/features/bug-evaluation/ReviewForm';
import { useAuth } from '../../hooks/useAuth';
import { useBugEvaluationDetail } from '../../hooks/useBugEvaluation';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business バグ評価詳細・レビューページ。Gemini評価結果の全14項目表示と人間によるスコア修正UIを提供。i18n翻訳対応
 * @graph-connects useBugEvaluationDetail [calls] バグ評価詳細フック
 * @graph-connects BugDetailsCard [calls] バグ詳細セクション描画
 * @graph-connects CauseProjectCard [calls] 起因案件の確認と付け替え描画
 * @graph-connects EvaluationResultsCard [calls] 評価結果セクション描画
 * @graph-connects ReviewForm [calls] レビューフォーム描画
 * @graph-connects LoadingSkeleton [calls] ローディング表示
 */
/** @graph-connects none */
export const Route = createFileRoute('/bug-evaluation/$issueKey')({
  component: BugEvaluationDetailPage,
});

/** @graph-connects none */
function BugEvaluationDetailPage() {
  const { issueKey } = Route.useParams();
  const { t, i18n } = useTranslation();
  const currentLocale: 'ja' | 'en' = i18n.language.startsWith('ja') ? 'ja' : 'en';

  // 原文表示トグル: showOriginalの場合はソース言語でfetchし直す
  const [showOriginal, setShowOriginal] = useState(false);
  const effectiveLocale: 'ja' | 'en' = showOriginal ? 'ja' : currentLocale;
  const { data, isLoading } = useBugEvaluationDetail(issueKey, effectiveLocale);
  // 編集用に常に日本語データを取得（マスターデータの値が JA const と一致する必要がある）
  const { data: jaData } = useBugEvaluationDetail(issueKey, 'ja');
  const { user } = useAuth();
  const canEdit = (user?.portalRoles ?? []).some((role) =>
    REVIEW_ROLES.includes(role as (typeof REVIEW_ROLES)[number]),
  );

  const detail = data?.data;
  const isTranslated = detail?.isTranslated ?? false;

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (!detail) {
    return (
      <div className="flex items-center justify-center py-20 text-[var(--color-text-muted)]">
        {t('bugEvaluation.notFound', '評価データが見つかりません')}
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">{issueKey}</h1>
          <p className="mt-1 text-[var(--color-text-secondary)]">
            {t('bugEvaluation.evaluator', '評価者')}: {detail.evaluator} ・{' '}
            {detail.evaluatedAt.slice(0, 10)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isTranslated && (
            <button
              onClick={() => {
                setShowOriginal(!showOriginal);
              }}
              className="text-sm text-[var(--color-accent-primary)] hover:underline"
            >
              {showOriginal
                ? t('bugEvaluation.showTranslated', 'Show translated')
                : t('bugEvaluation.showOriginal', 'Show original')}
            </button>
          )}
          <a
            href={`https://air-closet.backlog.jp/view/${issueKey}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-[var(--color-accent-primary)] hover:underline"
          >
            {t('bugEvaluation.viewOnBacklog', 'Backlogで見る')}
          </a>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <Card className="p-4 text-center">
          <div className="text-xs text-[var(--color-text-secondary)]">i-point</div>
          <div className="text-3xl font-bold text-[var(--color-text-primary)]">{detail.iPoint}</div>
        </Card>
        <Card className="p-4 text-center">
          <div className="text-xs text-[var(--color-text-secondary)]">
            {t('bugEvaluation.negligenceLevel', '過失レベル')}
          </div>
          <div className="text-sm font-medium text-[var(--color-text-primary)] mt-1">
            {detail.negligenceLevel}
          </div>
        </Card>
        <Card className="p-4 text-center">
          <div className="text-xs text-[var(--color-text-secondary)]">
            {t('bugEvaluation.bugType', 'バグ種別')}
          </div>
          <div className="text-sm font-medium text-[var(--color-text-primary)] mt-1">
            {detail.bugType}
          </div>
        </Card>
        <Card className="p-4 text-center">
          <div className="text-xs text-[var(--color-text-secondary)]">
            {t('bugEvaluation.review', 'レビュー')}
          </div>
          <div className="text-sm font-medium mt-1">
            {detail.isHumanReviewed ? (
              <span className="text-green-600">{detail.reviewedBy}</span>
            ) : (
              <span className="text-yellow-600">{t('bugEvaluation.unreviewed', '未レビュー')}</span>
            )}
          </div>
        </Card>
      </div>

      <BugDetailsCard detail={detail} isTranslated={isTranslated} showOriginal={showOriginal} />
      <CauseProjectCard issueKey={issueKey} canEdit={canEdit} />
      <EvaluationResultsCard
        detail={detail}
        isTranslated={isTranslated}
        showOriginal={showOriginal}
      />
      <ReviewForm
        issueKey={issueKey}
        detail={detail}
        jaDetail={jaData?.data}
        currentLocale={currentLocale}
        canEdit={canEdit}
      />
    </div>
  );
}
